import { useState } from 'react';

export default function ServiceCTA() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        service: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 bg-gradient-to-br from-teal-600 to-teal-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <div className="text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
              <strong>お気軽にご相談ください</strong>
            </h2>
            <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 text-teal-50">
              岡山県内のリフォーム工事なら、25年の実績を持つおかやまリフォーム株式会社にお任せください。無料見積もり・現地調査実施中です。
            </p>
            
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-start">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-500 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-lg sm:text-xl text-white"></i>
                </div>
                <div className="min-w-0">
                  <p className="text-teal-100 text-xs sm:text-sm">お電話でのお問い合わせ</p>
                  <a href="tel:086-012-3456" className="text-lg sm:text-2xl font-bold text-white hover:text-teal-200 transition-colors cursor-pointer whitespace-nowrap">
                    086-012-3456
                  </a>
                  <p className="text-teal-200 text-xs sm:text-sm">営業時間：9:00〜18:00<br className="sm:hidden" />（日曜・祝日除く）</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-500 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-lg sm:text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-teal-100 text-xs sm:text-sm">メールでのお問い合わせ</p>
                  <p className="text-white font-semibold text-sm sm:text-base">24時間受付中</p>
                  <p className="text-teal-200 text-xs sm:text-sm">お返事は営業時間内にいたします</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-500 rounded-lg flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg sm:text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-teal-100 text-xs sm:text-sm">対応エリア</p>
                  <p className="text-white font-semibold text-sm sm:text-base">岡山市・倉敷市・岡山県内全域</p>
                  <p className="text-teal-200 text-xs sm:text-sm">出張費無料（一部地域除く）</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-6 sm:p-8 shadow-xl">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">無料見積もり依頼</h3>
            
            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-start">
                  <i className="ri-check-circle-line text-green-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <p className="text-green-800 text-xs sm:text-sm">お問い合わせありがとうございます。担当者より折り返しご連絡いたします。</p>
                </div>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex items-start">
                  <i className="ri-error-warning-line text-red-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <p className="text-red-800 text-xs sm:text-sm">送信に失敗しました。お電話でお問い合わせください。</p>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div>
                <label htmlFor="name" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="山田太郎"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  電話番号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="086-012-3456"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  メールアドレス
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder="example@email.com"
                />
              </div>
              
              <div>
                <label htmlFor="service" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ご希望のサービス
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">選択してください</option>
                  <option value="residential">住宅リフォーム</option>
                  <option value="commercial">店舗工事</option>
                  <option value="repair">修繕工事</option>
                  <option value="consultation">相談のみ</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="message" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  ご相談内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="リフォームのご要望やご質問をお聞かせください（500文字以内）"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full bg-teal-600 text-white py-2.5 sm:py-3 px-6 rounded-lg text-sm sm:text-base font-semibold hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '無料見積もりを依頼する'}
              </button>
            </form>
            
            <p className="text-xs text-gray-500 mt-3 sm:mt-4 text-center">
              お客様の個人情報は適切に管理し、営業目的以外には使用いたしません。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
